import React, { useEffect, useMemo, useState } from 'react';
import { deleteField, doc, getDoc, writeBatch } from 'firebase/firestore';
import { db } from '../../../shared/storage/firebase';
import type { Squad } from '../../../types';
import {
  LOCATION_TEACHING_LOGISTICS_FIELDS,
} from '../utils/teacherLogisticsCompleteness';
import '../styles/RequiredTeacherLogisticsModal.css';

type TeacherEditableKey =
  | 'wifiUsername'
  | 'wifiPassword'
  | 'arrivalProcedure'
  | 'dismissalProcedure'
  | 'lateArrivalProcedure'
  | 'bathroomRules'
  | 'hallwayRules'
  | 'parkingInstructions'
  | 'classroomName'
  | 'hasProjectorOrTv'
  | 'projectorConnectionInstructions'
  | 'additionalNotes';

type FieldDef = {
  key: TeacherEditableKey;
  label: string;
  help: string;
  kind: 'text' | 'textarea' | 'booleanSelect';
  placeholder?: string;
  rows?: number;
  fullWidth?: boolean;
  /**
   * When provided, this field is only required/shown when condition is met.
   * We still allow "I don't know" to satisfy it when shown.
   */
  showWhen?: (draft: Partial<Squad>) => boolean;
  requiredWhen?: (draft: Partial<Squad>) => boolean;
};

const FIELDS: FieldDef[] = [
  {
    key: 'wifiUsername',
    label: 'WiFi username',
    kind: 'text',
    placeholder: 'WiFi username',
    help: '',
  },
  {
    key: 'wifiPassword',
    label: 'WiFi password',
    kind: 'text',
    placeholder: 'WiFi password',
    help: '',
  },
  {
    key: 'arrivalProcedure',
    label: 'Arrival procedure',
    kind: 'textarea',
    rows: 5,
    placeholder: 'What should your substitute do when they arrive?',
    help: 'Where to check in, which entrance to use, who to ask for, and any key steps.',
  },
  {
    key: 'dismissalProcedure',
    label: 'Dismissal procedure',
    kind: 'textarea',
    rows: 5,
    placeholder: 'How does dismissal work?',
    help: 'How students are dismissed, where they go, and any important supervision steps.',
  },
  {
    key: 'lateArrivalProcedure',
    label: 'Late arrival rules',
    kind: 'textarea',
    rows: 4,
    placeholder: 'If your substitute arrives late, what should they do?',
    help: 'Front office steps, bell/buzzer, sign-in, etc.',
  },
  {
    key: 'bathroomRules',
    label: 'Bathroom rules',
    kind: 'textarea',
    rows: 4,
    placeholder: 'Bathroom policy/rules',
    help: 'Anything the substitute needs to know to keep students safe and follow school policy.',
  },
  {
    key: 'hallwayRules',
    label: 'Hallway rules',
    kind: 'textarea',
    rows: 4,
    placeholder: 'Hallway policy/rules',
    help: 'Lining up, escorts, passes, noise expectations, etc.',
  },
  {
    key: 'parkingInstructions',
    label: 'Parking instructions',
    kind: 'textarea',
    rows: 4,
    placeholder: 'Where to park, where to enter, etc.',
    help: 'Parking lot, staff entrance, where to walk in, and any restrictions.',
  },
  {
    key: 'classroomName',
    label: 'Classroom name',
    kind: 'text',
    placeholder: 'e.g., Room 201',
    help: 'The specific room/location where class takes place.',
  },
  {
    key: 'hasProjectorOrTv',
    label: 'Projector / TV available',
    kind: 'booleanSelect',
    help: 'Please choose Yes/No, or check “I don’t know.” This helps your substitute plan ahead.',
  },
  {
    key: 'projectorConnectionInstructions',
    label: 'How to connect to projector',
    kind: 'textarea',
    rows: 4,
    fullWidth: true,
    placeholder: 'HDMI adapter? AirPlay? Who to ask? Any gotchas?',
    help: 'Only required if a projector/TV is available (or check “I don’t know”).',
    showWhen: (draft) => (draft as any)?.hasProjectorOrTv === true,
    requiredWhen: (draft) => (draft as any)?.hasProjectorOrTv === true,
  },
  {
    key: 'additionalNotes',
    label: 'Teaching logistics notes',
    kind: 'textarea',
    rows: 6,
    fullWidth: true,
    placeholder: 'Anything else that will help a substitute have a smooth class (materials location, setup, who to ask for help, etc.)',
    help: 'These notes are meant to help your substitute do a great job and have a good experience teaching.',
  },
];

function isBlank(v: unknown): boolean {
  if (v === undefined || v === null) return true;
  if (typeof v === 'string') return v.trim() === '';
  return false;
}

function hasAnswer(def: FieldDef, draft: Partial<Squad>): boolean {
  if (def.kind === 'booleanSelect') {
    const v = (draft as any)[def.key];
    return v === true || v === false;
  }
  return !isBlank((draft as any)[def.key]);
}

export default function RequiredTeacherLogisticsModal(props: {
  isOpen: boolean;
  squadId: string;
  currentUserId: string | null | undefined;
  title?: string;
  onClose: () => void;
  onContinue: () => void;
}) {
  const { isOpen, squadId, currentUserId, onClose, onContinue } = props;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [draft, setDraft] = useState<Partial<Squad>>({});
  const [unknown, setUnknown] = useState<Record<TeacherEditableKey, boolean>>({} as any);
  const [locationId, setLocationId] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    if (!squadId) return;

    const run = async () => {
      try {
        setLoading(true);
        setLoadError(null);
        setSaveError(null);
        setLocationId('');

        const snap = await getDoc(doc(db, 'squads', squadId));
        if (!snap.exists()) throw new Error('Squad not found.');
        const s = { ...(snap.data() as any), id: snap.id } as Squad;
        const nextLocationId = String((s as any).locationId || '').trim();
        if (!nextLocationId) throw new Error('This squad does not have a canonical location.');
        const locationSnap = await getDoc(doc(db, 'locations', nextLocationId));
        if (!locationSnap.exists()) throw new Error('Location not found.');
        const location = locationSnap.data() as any;
        setLocationId(nextLocationId);

        const squadUnknown: any = (s as any)?.teacherLogisticsUnknown || {};
        const locationUnknown: any = location?.teacherLogisticsUnknown || {};
        const nextUnknown: any = {};
        for (const f of FIELDS) {
          // Only keep "unknown" if the field truly has no answer.
          const isLocationField = LOCATION_TEACHING_LOGISTICS_FIELDS.includes(f.key as any);
          const source = isLocationField ? location : s;
          const unknownMarked = (isLocationField ? locationUnknown : squadUnknown)?.[f.key] === true;
          const answered = hasAnswer(f, source as any);
          nextUnknown[f.key] = unknownMarked && !answered;
        }

        setDraft({
          wifiUsername: location.wifiUsername || (s as any).wifiUsername || '',
          wifiPassword: location.wifiPassword || (s as any).wifiPassword || '',
          arrivalProcedure: location.arrivalProcedure || (s as any).arrivalProcedure || '',
          dismissalProcedure: location.dismissalProcedure || (s as any).dismissalProcedure || '',
          lateArrivalProcedure: location.lateArrivalProcedure || (s as any).lateArrivalProcedure || '',
          bathroomRules: location.bathroomRules || (s as any).bathroomRules || '',
          hallwayRules: location.hallwayRules || (s as any).hallwayRules || '',
          parkingInstructions: location.parkingInstructions || (s as any).parkingInstructions || '',
          classroomName: (s as any).classroomName || '',
          hasProjectorOrTv: (s as any).hasProjectorOrTv ?? null,
          projectorConnectionInstructions: (s as any).projectorConnectionInstructions || '',
          additionalNotes: (s as any).additionalNotes || '',
        } as any);
        setUnknown(nextUnknown);
      } catch (e: any) {
        setLoadError(String(e?.message || 'Failed to load squad details.'));
      } finally {
        setLoading(false);
      }
    };

    void run();
  }, [isOpen, squadId]);

  const visibleFields = useMemo(() => {
    return FIELDS.filter(f => (typeof f.showWhen === 'function' ? f.showWhen(draft) : true));
  }, [draft]);

  const missingKeys = useMemo(() => {
    const missing: TeacherEditableKey[] = [];
    for (const f of visibleFields) {
      const isUnknown = unknown?.[f.key] === true;
      if (isUnknown) continue;

      const required = typeof f.requiredWhen === 'function' ? f.requiredWhen(draft) : true;
      if (!required) continue;

      if (f.kind === 'booleanSelect') {
        const v = (draft as any)[f.key];
        if (v !== true && v !== false) missing.push(f.key);
        continue;
      }

      const v = (draft as any)[f.key];
      if (isBlank(v)) missing.push(f.key);
    }
    return missing;
  }, [draft, unknown, visibleFields]);

  const canContinue = !loading && !saving && missingKeys.length === 0;

  const saveAndContinue = async () => {
    if (!canContinue) return;
    try {
      setSaving(true);
      setSaveError(null);

      const now = Date.now();
      const locationUnknown: any = {};
      const squadUnknown: any = {};
      Object.entries(unknown || {}).filter(([, value]) => value === true).forEach(([key]) => {
        if (LOCATION_TEACHING_LOGISTICS_FIELDS.includes(key as any)) locationUnknown[key] = true;
        else squadUnknown[key] = true;
      });

      const locationPayload: any = {
        wifiUsername: String((draft as any).wifiUsername || '').trim(),
        wifiPassword: String((draft as any).wifiPassword || '').trim(),
        arrivalProcedure: String((draft as any).arrivalProcedure || '').trim(),
        dismissalProcedure: String((draft as any).dismissalProcedure || '').trim(),
        lateArrivalProcedure: String((draft as any).lateArrivalProcedure || '').trim(),
        bathroomRules: String((draft as any).bathroomRules || '').trim(),
        hallwayRules: String((draft as any).hallwayRules || '').trim(),
        parkingInstructions: String((draft as any).parkingInstructions || '').trim(),
        teacherLogisticsUnknown: Object.keys(locationUnknown).length > 0 ? locationUnknown : deleteField(),
        teacherLogisticsUpdatedAt: now,
        teacherLogisticsUpdatedBy: currentUserId || null,
        teacherLogisticsLastEditedFromSquadId: squadId,
      };
      const squadPayload: any = {
        classroomName: String((draft as any).classroomName || '').trim(),
        hasProjectorOrTv: unknown.hasProjectorOrTv ? null : ((draft as any).hasProjectorOrTv ?? null),
        projectorConnectionInstructions: String((draft as any).projectorConnectionInstructions || '').trim(),
        additionalNotes: String((draft as any).additionalNotes || '').trim(),
        detailsUpdatedAt: now,
        detailsUpdatedBy: currentUserId || null,
        teacherDetailsUpdatedAt: now,
        teacherDetailsUpdatedBy: currentUserId || null,
        teacherLogisticsUnknown: Object.keys(squadUnknown).length > 0 ? squadUnknown : deleteField(),
      };

      if (!locationId) throw new Error('This squad does not have a canonical location.');
      const batch = writeBatch(db);
      batch.update(doc(db, 'locations', locationId), locationPayload);
      batch.update(doc(db, 'squads', squadId), squadPayload);
      await batch.commit();
      onContinue();
    } catch (e: any) {
      console.error('Failed to save required sub-request logistics', { squadId, locationId, error: e });
      setSaveError(String(e?.message || 'Failed to save squad details.'));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="rtl-overlay" onClick={() => { if (!saving) onClose(); }}>
      <div className="rtl-modal" onClick={(e) => e.stopPropagation()}>
        <div className="rtl-title">
          {props.title || 'Squad details required before requesting a sub'}
        </div>
        <div className="rtl-sub">
          These details are <b>required</b> before you can request a sub. They help your substitute walk in prepared and have a great experience teaching.
          <br />
          <span className="rtl-sub-muted">
            If you truly don’t know something, check “I don’t know.” Your substitute will need to figure it out when they arrive.
          </span>
        </div>

        {loadError && <div className="rtl-error" role="alert">{loadError}</div>}
        {saveError && <div className="rtl-error" role="alert">{saveError}</div>}

        {loading ? (
          <div className="rtl-loading">Loading…</div>
        ) : (
          <>
            <div className="rtl-grid">
              {visibleFields.map((f) => {
                const isUnknown = unknown?.[f.key] === true;
                const missing = missingKeys.includes(f.key);
                const fieldId = `rtl_${f.key}`;

                const answered = hasAnswer(f, draft);
                const showUnknownToggle = !answered; // only allow unknown when empty/unanswered

                const onToggleUnknown = (checked: boolean) => {
                  setUnknown(prev => ({ ...(prev as any), [f.key]: checked }));
                  if (checked) {
                    // Enforce: unknown means no typed answer.
                    setDraft(prev => ({ ...prev, [f.key]: f.kind === 'booleanSelect' ? null : '' } as any));
                  }
                };

                const commonProps = {
                  id: fieldId,
                  disabled: saving || isUnknown,
                  'aria-invalid': missing ? 'true' : undefined,
                } as any;

                return (
                  <div
                    key={f.key}
                    className={`rtl-field ${f.fullWidth ? 'is-full' : ''} ${missing ? 'is-missing' : ''}`}
                  >
                    <div className="rtl-label-row">
                      <label className="rtl-label" htmlFor={fieldId}>
                        {f.label}
                      </label>
                      {answered ? (
                        <span className="rtl-answered" aria-label="Completed" title="Completed">✓</span>
                      ) : showUnknownToggle ? (
                        <label className="rtl-unknown" title="Only use this if you truly don’t know yet.">
                          <input
                            type="checkbox"
                            checked={isUnknown}
                            onChange={(e) => onToggleUnknown(e.target.checked)}
                            disabled={saving}
                          />
                          <span>I don’t know</span>
                        </label>
                      ) : null}
                    </div>
                    {f.help ? <div className="rtl-help">{f.help}</div> : null}

                    {f.kind === 'text' && (
                      <input
                        {...commonProps}
                        type="text"
                        value={String((draft as any)[f.key] ?? '')}
                        placeholder={f.placeholder}
                        onChange={(e) => {
                          const v = e.target.value;
                          setDraft(prev => ({ ...prev, [f.key]: v } as any));
                          if (!isBlank(v) && unknown?.[f.key]) {
                            setUnknown(prev => ({ ...(prev as any), [f.key]: false }));
                          }
                        }}
                      />
                    )}

                    {f.kind === 'textarea' && (
                      <textarea
                        {...commonProps}
                        rows={f.rows || 4}
                        value={String((draft as any)[f.key] ?? '')}
                        placeholder={f.placeholder}
                        onChange={(e) => {
                          const v = e.target.value;
                          setDraft(prev => ({ ...prev, [f.key]: v } as any));
                          if (!isBlank(v) && unknown?.[f.key]) {
                            setUnknown(prev => ({ ...(prev as any), [f.key]: false }));
                          }
                        }}
                      />
                    )}

                    {f.kind === 'booleanSelect' && (
                      <select
                        {...commonProps}
                        value={(() => {
                          const v = (draft as any)[f.key];
                          if (v === true) return 'true';
                          if (v === false) return 'false';
                          return '';
                        })()}
                        onChange={(e) => {
                          const v = e.target.value;
                          setDraft(prev => ({
                            ...prev,
                            [f.key]: v === '' ? null : v === 'true',
                          } as any));
                          if (v !== '' && unknown?.[f.key]) {
                            setUnknown(prev => ({ ...(prev as any), [f.key]: false }));
                          }
                        }}
                      >
                        <option value="">Select…</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    )}
                  </div>
                );
              })}
            </div>

            {missingKeys.length > 0 && (
              <div className="rtl-missing-hint" role="status">
                Please complete all sections (or check “I don’t know”) before continuing.
              </div>
            )}
          </>
        )}

        <div className="rtl-actions">
          <button type="button" className="rtl-btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="rtl-btn rtl-btn-primary"
            onClick={() => void saveAndContinue()}
            disabled={!canContinue}
            title={!canContinue ? 'Complete all sections or mark “I don’t know”.' : undefined}
          >
            {saving ? 'Saving…' : 'Save + Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}
